import React, { useState } from 'react';
import { Search, MoreHorizontal, UserPlus, Phone, CreditCard, ShieldAlert, Download, Monitor, EyeOff, MapPin, Pencil, Trash2, History } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { PaymentDialog } from '@/components/collections/PaymentDialog';
import { ClientFormDialog } from '@/components/clients/ClientFormDialog';
import { ClientHistoryDialog } from '@/components/clients/ClientHistoryDialog';
import { useClients, generatePDFPassbook, useDeleteClient, useUpdateClient } from '@/hooks/use-api';
import type { SusuClient } from '@shared/types';
import { Skeleton } from '@/components/ui/skeleton';
export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [paymentClient, setPaymentClient] = useState<SusuClient | null>(null);
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [territoryFilter, setTerritoryFilter] = useState('All');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<SusuClient | null>(null);
  const [historyClient, setHistoryClient] = useState<SusuClient | null>(null);
  const deleteClient = useDeleteClient();
  const updateClient = useUpdateClient();
  const { data, isLoading } = useClients({
    atRisk: activeTab === 'at-risk',
    territory: territoryFilter === 'All' ? undefined : territoryFilter
  });
  const filteredClients = (data?.items || []).filter(client =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.phone.includes(search)
  );
  return (
    <AppLayout container className="bg-accent/20">
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Clients Directory</h1>
            <p className="text-muted-foreground mt-1">
              {isKioskMode ? "Secure Public Interface" : "Institutional client relationship management."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className={cn("h-11 px-6 rounded-xl border-border", isKioskMode && "bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-lg")}
              onClick={() => setIsKioskMode(!isKioskMode)}
            >
              {isKioskMode ? <EyeOff className="h-4 w-4 mr-2" /> : <Monitor className="h-4 w-4 mr-2" />}
              {isKioskMode ? "Exit Kiosk" : "Kiosk Mode"}
            </Button>
            {!isKioskMode && (
              <Button onClick={() => setIsCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6 rounded-xl shadow-lg shadow-primary/20">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            )}
          </div>
        </div>
        {!isKioskMode && (
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card p-4 rounded-2xl border border-border shadow-sm">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
              <TabsList className="bg-muted/50 border border-border p-1">
                <TabsTrigger value="all" className="data-[state=active]:bg-card data-[state=active]:text-foreground rounded-lg">All Clients</TabsTrigger>
                <TabsTrigger value="at-risk" className="data-[state=active]:bg-card data-[state=active]:text-destructive text-destructive/80 rounded-lg">
                  <ShieldAlert className="h-3 w-3 mr-1" /> At Risk
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or phone..."
                  className="pl-10 h-10 bg-card border-border focus-visible:ring-primary rounded-xl"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10 bg-card border-border rounded-xl">
                    <MapPin className="h-4 w-4 mr-2 text-primary" />
                    {territoryFilter}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-xl">
                  <DropdownMenuItem onClick={() => setTerritoryFilter('All')}>All Territories</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTerritoryFilter('Central Market')}>Central Market</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTerritoryFilter('South Ridge')}>South Ridge</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTerritoryFilter('West End')}>West End</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
        <div className={cn(
          "rounded-2xl border border-border bg-card overflow-hidden shadow-soft",
          isKioskMode && "border-primary border-2"
        )}>
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="font-bold text-foreground px-6 py-4">Client Name</TableHead>
                {!isKioskMode && <TableHead className="font-bold text-foreground">Phone</TableHead>}
                <TableHead className="font-bold text-foreground text-right">Passbook Balance</TableHead>
                {!isKioskMode && <TableHead className="font-bold text-foreground">Territory</TableHead>}
                <TableHead className="font-bold text-foreground">Last Activity</TableHead>
                <TableHead className="text-right px-6"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className="border-border">
                    <TableCell colSpan={isKioskMode ? 4 : 6} className="px-6 py-4"><Skeleton className="h-6 w-full rounded-md" /></TableCell>
                  </TableRow>
                ))
              ) : filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <TableRow key={client.id} className={cn(
                    "hover:bg-muted/20 transition-colors border-border",
                    client.isAtRisk && !isKioskMode && "bg-destructive/10"
                  )}>
                    <TableCell className="font-bold text-foreground px-6 py-4">
                      <div className="flex items-center gap-2">
                        {client.name}
                        {client.isAtRisk && !isKioskMode && (
                          <Badge variant="destructive" className="h-4 px-1.5 text-[8px] uppercase bg-destructive/10 text-destructive border-destructive/20">Overdue</Badge>
                        )}
                      </div>
                    </TableCell>
                    {!isKioskMode && <TableCell className="text-muted-foreground font-medium">{client.phone}</TableCell>}
                    <TableCell className="text-right font-mono font-bold text-foreground">
                      GHS {client.balance.toFixed(2)}
                    </TableCell>
                    {!isKioskMode && (
                      <TableCell>
                        <Badge variant="secondary" className="font-bold text-[10px] bg-muted text-muted-foreground hover:bg-muted/80 border-none px-2 py-0.5">
                          {client.territory}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground font-medium">
                      {client.lastPayment ? new Date(client.lastPayment).toLocaleDateString() : 'New Client'}
                    </TableCell>
                    <TableCell className="text-right px-6">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:text-primary/90 hover:bg-muted/50 h-8 rounded-lg font-bold"
                          onClick={() => generatePDFPassbook(client)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {isKioskMode ? "Statement" : "Passbook"}
                        </Button>
                        {!isKioskMode && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/50">
                                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-xl">
                              <DropdownMenuItem onClick={() => setPaymentClient(client)} className="font-medium">
                                <CreditCard className="h-4 w-4 mr-2 text-primary" /> Record Payment
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setHistoryClient(client)} className="font-medium">
                                <History className="h-4 w-4 mr-2 text-primary" /> View Full History
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                updateClient.mutateAsync({ id: client.id, data: { isAtRisk: !client.isAtRisk } });
                              }} className="font-medium">
                                <ShieldAlert className="h-4 w-4 mr-2 text-destructive" /> {client.isAtRisk ? 'Remove Flag' : 'Flag for Follow-up'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEditingClient(client)} className="font-medium">
                                <Pencil className="h-4 w-4 mr-2 text-primary" /> Edit Client
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  if (confirm(`Delete client ${client.name}? This action cannot be undone.`)) {
                                    deleteClient.mutate(client.id);
                                  }
                                }}
                                className="text-destructive font-medium"
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete Client
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-48 text-center text-muted-foreground font-medium">
                    No matching clients found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <PaymentDialog
        client={paymentClient}
        open={!!paymentClient}
        onOpenChange={(open) => !open && setPaymentClient(null)}
      />
      <ClientFormDialog
        client={editingClient}
        open={!!editingClient || isCreateOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditingClient(null);
            setIsCreateOpen(false);
          }
        }}
      />
      <ClientHistoryDialog
        client={historyClient}
        open={!!historyClient}
        onOpenChange={(open) => !open && setHistoryClient(null)}
      />
    </AppLayout>
  );
}